import React, { useState, useEffect } from 'react';
import { disableAnalytics, enableAnalytics } from '../services/analyticsConsent';
import { OPEN_COOKIE_PREFERENCES_EVENT } from '../services/cookiePreferences';

const CONSENT_KEY = 'scrolled_cookie_consent';

/**
 * Consent-first cookie preferences.
 * - Reject button is equally prominent.
 * - No scroll-consent; no pre-ticked boxes.
 * - Analytics is not loaded until the user accepts it.
 * - Settings and the public Privacy page let users reopen their preferences.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      disableAnalytics();
      setVisible(true);
      return;
    }

    try {
      const decision = JSON.parse(stored);
      setPrefs({ necessary: true, analytics: decision.analytics === true });
      if (decision.analytics === true) {
        enableAnalytics();
      } else {
        disableAnalytics();
      }
    } catch {
      localStorage.removeItem(CONSENT_KEY);
      disableAnalytics();
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
  }, []);

  const saveConsent = (decision) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...decision, ts: Date.now() }));
    setPrefs({ necessary: true, analytics: decision.analytics === true });
    if (decision.analytics === true) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
    setVisible(false);
  };

  const handleAcceptAll = () => saveConsent({ necessary: true, analytics: true, choice: 'accept_all' });
  const handleRejectAll = () => saveConsent({ necessary: true, analytics: false, choice: 'reject_all' });
  const handleSavePrefs = () => saveConsent({ ...prefs, choice: 'custom' });

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: '#0f172a',
        borderTop: '1px solid #1e293b',
        padding: '16px 20px',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{ maxWidth: '768px', margin: '0 auto' }}>
        <p style={{ color: '#cbd5e1', fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' }}>
          We use cookies to make ScrollEd work and, only with your consent, to measure how you engage with the app.
          {' '}
          <a href="/privacy" style={{ color: '#22B8E0', textDecoration: 'underline' }}>Privacy Policy</a>
        </p>

        {showDetails && (
          <div style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { key: 'necessary', label: 'Necessary', desc: 'Required for login and app functionality. Cannot be disabled.', locked: true },
              { key: 'analytics', label: 'Analytics', desc: 'Allows Google Analytics to measure app usage. It stays off unless you enable it.', locked: false },
            ].map(({ key, label, desc, locked }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: locked ? 'default' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={locked ? true : prefs[key]}
                  disabled={locked}
                  onChange={() => !locked && setPrefs(p => ({ ...p, [key]: !p[key] }))}
                  aria-label={label}
                  style={{ marginTop: '3px', accentColor: '#22B8E0' }}
                />
                <span style={{ color: '#94a3b8', fontSize: '13px' }}>
                  <strong style={{ color: '#e2e8f0' }}>{label}</strong> — {desc}
                  {locked && <em style={{ color: '#64748b' }}> (always active)</em>}
                </span>
              </label>
            ))}
            <button
              onClick={handleSavePrefs}
              style={{
                marginTop: '8px',
                padding: '8px 18px',
                backgroundColor: '#22B8E0',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                alignSelf: 'flex-start',
              }}
            >
              Save my choices
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleRejectAll}
            style={{
              padding: '9px 20px',
              backgroundColor: '#334155',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Reject all
          </button>
          <button
            onClick={handleAcceptAll}
            style={{
              padding: '9px 20px',
              backgroundColor: '#22B8E0',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Accept all
          </button>
          <button
            onClick={() => setShowDetails(v => !v)}
            style={{
              padding: '9px 16px',
              backgroundColor: 'transparent',
              color: '#64748b',
              border: 'none',
              fontSize: '13px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {showDetails ? 'Hide details' : 'Manage preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
